import { fromMinorUnits } from '@lombok/shared';

/**
 * CSV files meant to be opened in Excel with the Venezuelan (es-VE) regional settings, where
 * the list separator is `;` and the decimal separator is `,`. With a comma delimiter Excel
 * there puts every row in a single column.
 *
 * Pure (no DOM) so it is unit-tested directly; `downloadCsv` is the only part that touches the page.
 */

const DELIMITER = ';';

/** RFC-4180 escape: quote-wrapped, embedded quotes doubled. */
function escapeCsvField(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * Text typed by people (customer names): a cell that starts with = + - @ would be run as a
 * formula when the file is opened in a spreadsheet, so it gets a leading apostrophe.
 */
export function csvText(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

/** Header row + one row per record, `\r\n` line endings; just the header when there are no rows. */
export function buildCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  return [headers, ...rows].map((cells) => cells.map(escapeCsvField).join(DELIMITER)).join('\r\n');
}

/** A plain day/month/year date (25/10/2026). Date-only values are kept as written; timestamps use local time. */
export function csvDate(iso: string | undefined): string {
  if (!iso) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Decimal USD with 2 places and a decimal comma (1250,00), never a localized currency string. */
export function csvMoney(minor: number): string {
  return fromMinorUnits(minor).toFixed(2).replace('.', ',');
}

/** Saves the text as a file. A UTF-8 BOM first so Excel reads the accents correctly. */
export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
